<template>
    <div class="p-6 w-full">
        <div class="mb-6">
            <h1 class="text-3xl font-bold text-rose-800 mb-2">Pacienți</h1>
            <p class="text-gray-600">Lista tuturor pacienților înregistrați</p>
        </div>
        <PatientsFilters v-model="filters" :doctors="doctors" />

        <div v-if="loading" class="text-center py-8">
            <i class="pi pi-spin pi-spinner text-4xl text-rose-600"></i>
            <p class="mt-4 text-gray-600">Se încarcă pacienții...</p>
        </div>

        <div v-else-if="patients.length === 0" class="text-center py-12 bg-gray-50 rounded-lg">
            <i class="pi pi-users text-6xl text-gray-400 mb-4"></i>
            <p class="text-xl text-gray-600">Nu există pacienți înregistrați</p>
            <Button label="Adaugă primul pacient" icon="pi pi-plus" @click="handleNewBuletin(null)" severity="success"
                class="mt-4" />
        </div>

        <div v-else class="bg-white rounded-lg shadow overflow-hidden">
            <table class="w-full">
                <thead class="bg-gradient-to-r from-rose-700 to-rose-900 text-white">
                    <tr>
                        <th class="px-4 py-3 text-left">Nume</th>
                        <th class="px-4 py-3 text-left">CNP</th>
                        <th class="px-4 py-3 text-left">Vârstă</th>
                        <th class="px-4 py-3 text-left">Sex</th>
                        <th class="px-4 py-3 text-left">Înălțime (cm)</th>
                        <th class="px-4 py-3 text-left">Greutate (kg)</th>
                        <th class="px-4 py-3 text-left">BSA (m²)</th>
                        <th class="px-4 py-3 text-left">Ultima vizită</th>
                        <th class="px-4 py-3 text-left">Examinat de</th>
                        <th class="px-4 py-3 text-center">Acțiuni</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="patient in filteredPatients" :key="patient.cnp"
                        class="border-b hover:bg-gray-50 transition-colors">
                        <td class="px-4 py-3 font-medium">{{ patient.name }}</td>
                        <td class="px-4 py-3 text-gray-600">{{ patient.cnp }}</td>
                        <td class="px-4 py-3">{{ patient.age }} ani</td>
                        <td class="px-4 py-3">
                            <span :class="patient.sex === 'F' ? 'text-pink-600' : 'text-blue-600'"
                                class="font-semibold">
                                {{ patient.sex }}
                            </span>
                        </td>
                        <td class="px-4 py-3">{{ patient.height }}</td>
                        <td class="px-4 py-3">{{ patient.weight }}</td>
                        <td class="px-4 py-3">{{ patient.bsa }}</td>
                        <td class="px-4 py-3 text-gray-600">{{ patient.lastVisit }}</td>
                        <td class="px-4 py-3 text-gray-600 text-sm">
                            <span v-if="patient.lastDoctor">{{ patient.lastDoctor }}</span>
                            <span v-else class="text-gray-400">-</span>
                        </td>
                        <td class="px-4 py-3">
                            <div class="flex gap-2 justify-center">
                                <Button data-cy="new-exam" icon="pi pi-plus" @click="handleNewBuletin(patient)" severity="success"
                                    size="small" v-tooltip="'Buletin nou'" rounded />
                                <Button data-cy="view-last" icon="pi pi-eye" @click="handleViewLast(patient)" severity="info" size="small"
                                    v-tooltip="'Vezi ultimul buletin'" rounded />
                                <Button data-cy="view-history" icon="pi pi-history" @click="handleViewHistory(patient)" severity="warning"
                                    size="small" v-tooltip="'Vezi istoric'" rounded />
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>
</template>

<script setup>
import { computed, ref } from 'vue';
import { useRouter } from 'vue-router';
import Button from 'primevue/button';
import { db } from '../../firebase/config.js';
import { collection } from 'firebase/firestore';
import { useCollection } from 'vuefire';
import { flattenExam } from '../../composables/echo/useExamHelpers.js';
import { setSelectedPatient } from '../../stores/selectedPatient.js';
import PatientsFilters from './PatientsFilters.vue';

const router = useRouter();

const filters = ref({
    search: "",
    sex: "all",
    doctor: null,
    sortBy: "name_asc",
})
// read patients collection
const patientsRef = collection(db, 'patients')
const patientsData = useCollection(patientsRef)

// read exams collection
const examsRef = collection(db, 'exams')
const examsData = useCollection(examsRef)

const doctors = computed(() => {
    patients.value.map(p => p.lastDoctor).filter(Boolean)
});
// Compute patients with last visit info
const patients = computed(() => {
    if (!patientsData.value) return []

    return patientsData.value
        .map(patient => {
            // search last exam for this patient
            const patientExams = examsData.value?.filter(e => e.patientId === patient.cnp) || [];
            const lastExam = patientExams
                .filter(e => e.status !== 'void')
                .sort((a, b) => new Date(b.updatedAt || b.dateConsult) - new Date(a.updatedAt || a.dateConsult))[0];

            // Calculate age from birthDate
            let age = '-'
            if (patient.birthDate) {
                const birthDate = new Date(patient.birthDate);
                const today = new Date();
                age = today.getFullYear() - birthDate.getFullYear();
                const monthDiff = today.getMonth() - birthDate.getMonth();
                if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
                    age--;
                }
            }

            return {
                cnp: patient.cnp,
                lastName: patient.lastName,
                firstName: patient.firstName,
                name: `${patient.lastName} ${patient.firstName}`,
                age: age,
                sex: patient.sex,
                birthDate: patient.birthDate,
                phone: patient.phone,
                notes: patient.notes,
                height: patient.height,
                weight: patient.weight,
                bsa: patient.bsa,
                lastVisit: lastExam?.dateConsult || '-',
                lastDoctor: lastExam?.doctorNameSnapshot || null,
                lastRecord: lastExam
                    ? {
                        ...flattenExam(lastExam),
                        id: lastExam.id,
                        status: lastExam.status,
                        locked: lastExam.locked
                    }
                    : null
            }
        }).sort((a, b) => a.name.localeCompare(b.name))
})

const loading = computed(() => !patientsData.value)

//actions (store + router)
const handleNewBuletin = (patient = null) => {
    // new exam: just patient info, no loadLast
    setSelectedPatient(patient);
    router.push({ name: 'formular' });
};

const handleViewLast = (patient) => {
    // load last exam for this patient
    if (!patient.lastRecord) return;

    setSelectedPatient({
        ...patient,
        loadLast: true,
        lastRecord: patient.lastRecord
    });

    router.push({ name: 'formular' });
};

const handleViewHistory = (patient) => {
    // load patient history
    setSelectedPatient(patient);
    router.push({ name: 'istoric' });
};

const filteredPatients = computed(() => {
    const f = filters.value;
    let list = [...patients.value];

    // search
    const q = (f.search || "").trim().toLowerCase();
    if (q) {
        list = list.filter(p => {
            const hay = [
                p.name,
                p.cnp,
                p.phone,
            ].filter(Boolean).join(" ").toLowerCase();
            return hay.includes(q);
        });
    }

    // sex
    if (f.sex !== "all") {
        list = list.filter(p => p.sex === f.sex);
    }

    // doctor
    if (f.doctor) {
        list = list.filter(p => p.lastDoctor === f.doctor);
    }

    const toTime = (p) => {
        const d = p.lastRecord?.updatedAt || p.lastRecord?.dateConsult || null;
        return d ? new Date(d).getTime() : 0;
    };

    // sort
    if (f.sortBy === "name_asc") {
        list.sort((a, b) => a.name.localeCompare(b.name));
    } else if (f.sortBy === "name_desc") {
        list.sort((a, b) => b.name.localeCompare(a.name));
    }
    else if (f.sortBy === "last_desc") {
        list.sort((a, b) => toTime(b) - toTime(a));
    } else if (f.sortBy === "last_asc") {
        list.sort((a, b) => toTime(a) - toTime(b));
    }

    return list;
});
</script>
